// A DocumentsBackend for the Capture DocumentsPanel that routes a touchpoint's
// file links through ryugu's per-program write gate instead of straight to
// tesoro. The bytes still upload via tesoro's presigned PUT; only the
// touchpoint<->artifact link (attach / list / detach) crosses the authorization
// boundary, so only those hit ryugu. Classify stays artifact-scoped (same-tenant
// metadata, not the flagged link-write path) but invalidates this backend's key.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/authContext';
import { artifactClient, deliveryClient } from '../../../lib/connect/clients';
import { uploadAttachmentViaTesoro } from '../../../lib/uploadAttachment';
const KEY = 'touchpoint-files';
// The Documents-panel-facing hooks below narrow ryugu's TouchpointFileProto
// down to DocumentRow (DocumentsPanel's generic contract), which drops
// mime_type. The touchpoint review flow needs mime_type itself (to spot an
// audio attachment - transcription isn't supported, see
// TouchpointExtractor.Extract) and every attached artifact id (to source a
// WORKFLOW_KIND_TOUCHPOINT run) - so this reads the same RPC directly rather
// than going through the panel's narrowed shape. Deliberately its own query
// key: it caches the full proto, not DocumentRow[].
export function useTouchpointFiles(programId, touchpointId) {
    return useQuery({
        // 'full' is a suffix, not a prefix: the mutations below invalidate
        // [KEY, programId, touchpointId], and TanStack Query's default
        // invalidation matches by positional prefix, so this key must start
        // with that same triple to be caught by those invalidations too.
        queryKey: [
            KEY,
            programId,
            touchpointId,
            'full'
        ],
        enabled: !!programId && !!touchpointId,
        queryFn: async ()=>{
            const resp = await deliveryClient.listTouchpointFiles({
                programId,
                touchpointId
            });
            return resp.files;
        }
    });
}
// Links one already-uploaded artifact (bytes already in tesoro, see
// TouchpointFileStaging in CustomerTouchpointsTab.tsx) to a touchpoint that
// now exists. Split out from makeTouchpointFileBackend's useUpload because
// the create flow uploads BEFORE the touchpoint exists and links AFTER —
// two separate RPCs, not the single upload-then-link the drawer's backend
// does once a touchpoint id is already in hand.
export function useAttachTouchpointFile(programId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ touchpointId, artifactId })=>{
            await deliveryClient.attachTouchpointFile({
                programId,
                touchpointId,
                artifactId
            });
        },
        onSuccess: (_data, { touchpointId })=>{
            qc.invalidateQueries({
                queryKey: [
                    KEY,
                    programId,
                    touchpointId
                ]
            });
        }
    });
}
export function makeTouchpointFileBackend(programId) {
    function useList(_entityType, touchpointId) {
        return useQuery({
            queryKey: [
                KEY,
                programId,
                touchpointId
            ],
            enabled: !!programId && !!touchpointId,
            queryFn: async ()=>{
                const resp = await deliveryClient.listTouchpointFiles({
                    programId,
                    touchpointId
                });
                return resp.files;
            }
        });
    }
    function useUpload(_entityType, touchpointId) {
        const qc = useQueryClient();
        const { user } = useAuth();
        return useMutation({
            mutationFn: async (file)=>{
                if (!user || !user.tenant_id) throw new Error('Not signed in');
                const uploaded = await uploadAttachmentViaTesoro(file, {
                    tenantId: user.tenant_id,
                    ownerUserId: user.id,
                    autoProcess: false
                });
                await deliveryClient.attachTouchpointFile({
                    programId,
                    touchpointId,
                    artifactId: uploaded.artifactId
                });
                return uploaded;
            },
            onSuccess: ()=>qc.invalidateQueries({
                    queryKey: [
                        KEY,
                        programId,
                        touchpointId
                    ]
                }),
            onError: (err)=>toast.error(err instanceof Error ? err.message : 'Failed to upload document')
        });
    }
    function useClassify(_entityType, touchpointId) {
        const qc = useQueryClient();
        return useMutation({
            mutationFn: (args)=>artifactClient.classifyArtifact(args),
            onSuccess: ()=>qc.invalidateQueries({
                    queryKey: [
                        KEY,
                        programId,
                        touchpointId
                    ]
                }),
            onError: (err)=>toast.error(err instanceof Error ? err.message : 'Failed to classify document')
        });
    }
    function useUnlink(_entityType, touchpointId) {
        const qc = useQueryClient();
        return useMutation({
            mutationFn: (artifactId)=>deliveryClient.detachTouchpointFile({
                    programId,
                    touchpointId,
                    artifactId
                }),
            onSuccess: ()=>qc.invalidateQueries({
                    queryKey: [
                        KEY,
                        programId,
                        touchpointId
                    ]
                }),
            onError: (err)=>toast.error(err instanceof Error ? err.message : 'Failed to remove document')
        });
    }
    return {
        useList,
        useUpload,
        useClassify,
        useUnlink
    };
}
